import {
  DocumentIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  PhotographIcon,
} from '@heroicons/react/outline';

export type FileType = 'pdf' | 'word' | 'excel' | 'image' | 'unknown';

/**
 * Get file type from file name based on extension
 */
export function getFileTypeFromName(fileName: string): FileType {
  if (!fileName) return 'unknown';

  const extension = fileName.split('.').pop()?.toLowerCase() || '';

  if (['pdf'].includes(extension)) {
    return 'pdf';
  }
  if (['doc', 'docx'].includes(extension)) {
    return 'word';
  }
  if (['xls', 'xlsx'].includes(extension)) {
    return 'excel';
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
    return 'image';
  }

  return 'unknown';
}

/**
 * Get appropriate icon component for file type
 */
export function getFileTypeIcon(
  fileType: FileType,
): React.ComponentType<{ className?: string }> {
  switch (fileType) {
    case 'pdf':
      return DocumentTextIcon;
    case 'word':
      return DocumentDuplicateIcon;
    case 'excel':
      return DocumentDuplicateIcon;
    case 'image':
      return PhotographIcon;
    default:
      return DocumentIcon;
  }
}

/**
 * Format file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// File upload constants for assessment questions
export const FILE_UPLOAD_EXTENSIONS = {
  JPG: '.jpg',
  JPEG: '.jpeg',
  PNG: '.png',
  PDF: '.pdf',
  DOC: '.doc',
  DOCX: '.docx',
  XLS: '.xls',
  XLSX: '.xlsx',
} as const;

export const ALL_FILE_UPLOAD_EXTENSIONS = [
  FILE_UPLOAD_EXTENSIONS.JPG,
  FILE_UPLOAD_EXTENSIONS.JPEG,
  FILE_UPLOAD_EXTENSIONS.PNG,
  FILE_UPLOAD_EXTENSIONS.PDF,
  FILE_UPLOAD_EXTENSIONS.DOC,
  FILE_UPLOAD_EXTENSIONS.DOCX,
  FILE_UPLOAD_EXTENSIONS.XLS,
  FILE_UPLOAD_EXTENSIONS.XLSX,
] as const;

export const FILE_UPLOAD_MIME_TYPES = {
  [FILE_UPLOAD_EXTENSIONS.JPG]: 'image/jpeg',
  [FILE_UPLOAD_EXTENSIONS.JPEG]: 'image/jpeg',
  [FILE_UPLOAD_EXTENSIONS.PNG]: 'image/png',
  [FILE_UPLOAD_EXTENSIONS.PDF]: 'application/pdf',
  [FILE_UPLOAD_EXTENSIONS.DOC]: 'application/msword',
  [FILE_UPLOAD_EXTENSIONS.DOCX]:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  [FILE_UPLOAD_EXTENSIONS.XLS]: 'application/vnd.ms-excel',
  [FILE_UPLOAD_EXTENSIONS.XLSX]:
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as const;

export const FILE_UPLOAD_EXTENSION_LABELS = {
  [FILE_UPLOAD_EXTENSIONS.JPG]: 'JPG/JPEG',
  [FILE_UPLOAD_EXTENSIONS.JPEG]: 'JPG/JPEG',
  [FILE_UPLOAD_EXTENSIONS.PNG]: 'PNG',
  [FILE_UPLOAD_EXTENSIONS.PDF]: 'PDF',
  [FILE_UPLOAD_EXTENSIONS.DOC]: 'DOC/DOCX',
  [FILE_UPLOAD_EXTENSIONS.DOCX]: 'DOC/DOCX',
  [FILE_UPLOAD_EXTENSIONS.XLS]: 'XLS/XLSX',
  [FILE_UPLOAD_EXTENSIONS.XLSX]: 'XLS/XLSX',
} as const;

export type FileUploadExtensionConfig = {
  ext: string;
  label: string;
  mime: string;
  group?: string;
};

export const FILE_UPLOAD_EXTENSION_CONFIGS: FileUploadExtensionConfig[] = [
  {
    ext: FILE_UPLOAD_EXTENSIONS.JPG,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.JPG],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.JPG],
    group: 'jpg-jpeg',
  },
  {
    ext: FILE_UPLOAD_EXTENSIONS.JPEG,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.JPEG],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.JPEG],
    group: 'jpg-jpeg',
  },
  {
    ext: FILE_UPLOAD_EXTENSIONS.PNG,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.PNG],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.PNG],
  },
  {
    ext: FILE_UPLOAD_EXTENSIONS.PDF,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.PDF],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.PDF],
  },
  {
    ext: FILE_UPLOAD_EXTENSIONS.DOC,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.DOC],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.DOC],
    group: 'doc-docx',
  },
  {
    ext: FILE_UPLOAD_EXTENSIONS.DOCX,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.DOCX],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.DOCX],
    group: 'doc-docx',
  },
  {
    ext: FILE_UPLOAD_EXTENSIONS.XLS,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.XLS],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.XLS],
    group: 'xls-xlsx',
  },
  {
    ext: FILE_UPLOAD_EXTENSIONS.XLSX,
    label: FILE_UPLOAD_EXTENSION_LABELS[FILE_UPLOAD_EXTENSIONS.XLSX],
    mime: FILE_UPLOAD_MIME_TYPES[FILE_UPLOAD_EXTENSIONS.XLSX],
    group: 'xls-xlsx',
  },
];

/**
 * Get MIME type for a file extension
 */
export function getMimeTypeForExtension(ext: string): string | undefined {
  return FILE_UPLOAD_MIME_TYPES[
    ext.toLowerCase() as keyof typeof FILE_UPLOAD_MIME_TYPES
  ];
}

/**
 * Get display label for a file extension (grouped)
 */
export function getLabelForExtension(ext: string): string {
  return (
    FILE_UPLOAD_EXTENSION_LABELS[
      ext.toLowerCase() as keyof typeof FILE_UPLOAD_EXTENSION_LABELS
    ] || ext.toUpperCase().replace('.', '')
  );
}

/**
 * Get unique labels for a list of extensions (deduplicates grouped extensions)
 */
export function getUniqueExtensionLabels(extensions: string[]): string[] {
  const labels = new Set<string>();
  extensions.forEach((ext) => {
    labels.add(getLabelForExtension(ext));
  });
  return Array.from(labels);
}
